import { Button, Center, Icon, Spinner, Text, useToast } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { CoursesTable } from '../CoursesTable';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Plus } from 'lucide-react';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { CoursesFilters } from '../CoursesFilters';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import upperFirst from 'lodash-es/upperFirst.js';
import pickBy from 'lodash-es/pickBy.js';
import { emptyPaginationResponse, parseSortOrder, SortOrder } from '@duke-hq/lib/pagination';
import {
  CourseStatus,
  CourseStatusSchema,
} from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { CourseKind, CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { RouterOutputs, trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { testIds } from './testIds';
import { withPermission } from '../../withPermission';
import { getCourseKindLabel } from '../../lib/courseUtils';
import { CourseMarkSupersededModal } from '../CourseMarkSupersededModal/CourseMarkSupersededModal';

interface URLParams {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page?: string | undefined;
  kind: CourseKind | undefined;
  status: CourseStatus | undefined;
}

export const CoursesPageAdmin = withPermission('canManageCourseContent', () => {
  const [isSupersededModalOpen, setIsSupersededModalOpen] = useState(false);
  const { currentPage, pageSize, setPage } = usePagination();
  const [selectedCourse, setSelectedCourse] = useState<
    NonNullable<RouterOutputs['courses']['listCoursesWithPagination']>['items'][number] | null
  >(null);
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: 'created_at',
    sort: SortOrder.DESC,
    page: '1',
  });

  const querySearch = searchParams.get('search') ?? '';

  const URLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, search, kind, status, orderBy, sort, ...rest } = urlFilters;

    return {
      ...rest,
      orderBy: orderBy || 'created_at',
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
      kind: CourseKindSchema.safeParse(kind).data,
      status: CourseStatusSchema.safeParse(status).data,
      page: page ?? '1',
    };
  }, [searchParams]);

  const {
    data: paginatedCourses,
    isFetching,
    refetch,
  } = trpcAdmin.courses.listCoursesWithPagination.useQuery(
    {
      search: querySearch.trim(),
      filters: URLFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const createCourse = trpcAdmin.courses.create.useMutation();
  const duplicateCourse = trpcAdmin.courses.duplicate.useMutation({
    onSuccess() {
      void refetch();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title:
          error instanceof Error
            ? error.message
            : t`An error occurred while duplicating the course.`,
      });
    },
  });
  const deleteCourse = trpcAdmin.courses.archive.useMutation({
    onSuccess() {
      void refetch();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title:
          error instanceof Error ? error.message : t`An error occurred while deleting the course.`,
      });
    },
  });

  const markCourseSuperseded = (courseId: string) => {
    const course = paginatedCourses.items.find((c) => c.id === courseId);
    if (course) {
      setSelectedCourse(course);
      setIsSupersededModalOpen(true);
    }
  };

  const handleFilterChange = useCallback(
    (newFilters: Partial<URLParams>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const kindOptions = useMemo(() => {
    return CourseKindSchema.options.map((kind: CourseKind) => ({
      value: kind,
      label: getCourseKindLabel(kind),
    }));
  }, []);

  const statusOptions = useMemo(() => {
    return CourseStatusSchema.options.map((status: CourseStatus) => ({
      value: status,
      label: upperFirst(status.replace(/_/g, ' ').toLowerCase()),
    }));
  }, []);

  return (
    <>
      <TableViewLayout
        pageHeader={
          <PageHeading data-testid={testIds.TableHeading}>
            <Trans>Courses</Trans>
          </PageHeading>
        }
        pageActions={
          <Button
            leftIcon={<Icon as={Plus} />}
            size="sm"
            variant="primary"
            onClick={async () => {
              const course = await createCourse.mutateAsync({
                title: 'New Course Title',
              });
              if (typeof course.id === 'string') {
                navigate(`/courses/${course.id}`);
              }

              await refetch();
            }}
          >
            <Trans>Add a new course</Trans>
          </Button>
        }
        filters={
          <>
            <CoursesFilters
              filters={URLFilters}
              statusOptions={statusOptions}
              kindOptions={kindOptions}
              onChange={handleFilterChange}
            />
            <SearchInput maxW="search-input" />
          </>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={paginatedCourses.pagination.totalCount || 0}
              zero="0 courses"
              one="1 course"
              other="# courses"
            />
          </Text>
        }
        table={
          isFetching ? (
            <Center p={8}>
              <Spinner size="xl" transitionDelay={'2900ms'} />
            </Center>
          ) : paginatedCourses.items.length > 0 ? (
            <CoursesTable
              courses={paginatedCourses.items}
              urlParams={URLFilters}
              handleCourseDelete={deleteCourse.mutate}
              onFilters={handleFilterChange}
              onDuplicateCourseClick={duplicateCourse.mutate}
              onMarkSupersededCourseClick={markCourseSuperseded}
            />
          ) : (
            <Center p={8}>
              <Text textAlign="center">
                <Trans>No courses found</Trans>
              </Text>
            </Center>
          )
        }
        pagination={
          paginatedCourses.items.length > 0 ? (
            <Pagination pagination={paginatedCourses.pagination} onPageChange={setPage} />
          ) : null
        }
      />

      {selectedCourse !== null ? (
        <CourseMarkSupersededModal
          isOpen={isSupersededModalOpen}
          onClose={() => {
            setIsSupersededModalOpen(false);
            setSelectedCourse(null);
          }}
          onSupersedeCreateSuccess={() => {
            setIsSupersededModalOpen(false);
            setSelectedCourse(null);
            void refetch();
          }}
          currentCourse={selectedCourse}
        />
      ) : null}
    </>
  );
});
